import { useCallback, useEffect, useState } from "react";
import { Loader2, Ban, RotateCcw, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { formatDateIt } from "../../lib/constants";
import { inp } from "./AdminUI";

const OccurrenceEditor = ({ occ, onSave, onCancel }) => {
  const [time, setTime] = useState(occ.overridden ? occ.time : "");
  const [title, setTitle] = useState(occ.overridden ? occ.title : "");
  return (
    <div className="mt-2 grid grid-cols-[110px_1fr_auto] gap-2 items-center" data-testid={`occ-editor-${occ.date}`}>
      <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inp} data-testid={`occ-time-${occ.date}`} />
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo solo per questa data" className={inp} data-testid={`occ-title-${occ.date}`} />
      <div className="flex gap-1">
        <button data-testid={`occ-save-${occ.date}`} onClick={() => onSave(time, title)} className="w-9 h-9 grid place-items-center rounded-lg bg-wyd-accent text-wyd-bg"><Check size={16} /></button>
        <button data-testid={`occ-cancel-${occ.date}`} onClick={onCancel} className="w-9 h-9 grid place-items-center rounded-lg bg-wyd-surface2 text-wyd-muted"><X size={16} /></button>
      </div>
      <p className="col-span-3 text-xs text-wyd-muted">Lascia vuoti entrambi i campi per tornare a orario e titolo della serie.</p>
    </div>
  );
};

const OccurrenceRow = ({ occ, editing, onEdit, onSkip, onSave }) => (
  <li className={`rounded-xl px-4 py-3 border border-white/10 ${occ.skipped ? "opacity-50" : ""}`} data-testid={`occ-row-${occ.date}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className={`font-semibold ${occ.skipped ? "line-through" : ""}`}>{formatDateIt(occ.date)} · {occ.time || "orario libero"}</div>
        {occ.overridden && <div className="text-xs text-wyd-accent truncate">Modificata: {occ.title}</div>}
        {occ.skipped && <div className="text-xs text-wyd-muted">Data saltata</div>}
      </div>
      <div className="flex gap-2 shrink-0">
        {!occ.skipped && (
          <button data-testid={`occ-edit-${occ.date}`} onClick={onEdit} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-wyd-surface2 text-wyd-muted hover:text-white">
            <Pencil size={12} /> Modifica
          </button>
        )}
        <button data-testid={`occ-skip-${occ.date}`} onClick={() => onSkip(!occ.skipped)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs border border-wyd-accent text-wyd-accent hover:bg-wyd-accent/10">
          {occ.skipped ? <><RotateCcw size={12} /> Ripristina</> : <><Ban size={12} /> Salta questa data</>}
        </button>
      </div>
    </div>
    {editing && <OccurrenceEditor occ={occ} onSave={onSave} onCancel={onEdit} />}
  </li>
);

export const OccurrencesDialog = ({ event, onClose, onChanged }) => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    if (!event) return;
    api.get(`/admin/events/${event.id}/occurrences`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [event]);
  useEffect(() => { setItems(null); setEditing(null); load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); onChanged(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const skip = (date, value) => run(() => api.post(`/admin/events/${event.id}/skip`, { date, skip: value }), value ? "Data saltata" : "Data ripristinata");
  const save = (date, time, title) => run(async () => {
    await api.put(`/admin/events/${event.id}/occurrence`, { date, time, title });
    setEditing(null);
  }, "Data aggiornata");

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-wyd-surface border border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="occurrences-dialog">
        <DialogHeader><DialogTitle className="font-display">Date dei prossimi 60 giorni</DialogTitle></DialogHeader>
        {event && <p className="text-sm text-wyd-muted -mt-2">{event.title} · {event.recurrence_label}</p>}
        {items === null ? <Loader2 className="animate-spin mx-auto my-6" /> : items.length === 0 ? (
          <p className="text-sm text-wyd-muted py-6 text-center">Nessuna data nei prossimi 60 giorni.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((o) => (
              <OccurrenceRow key={o.date} occ={o} editing={editing === o.date}
                onEdit={() => setEditing(editing === o.date ? null : o.date)}
                onSkip={(v) => skip(o.date, v)} onSave={(t, ti) => save(o.date, t, ti)} />
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
